import type { AgentTaskModel } from "@fencyai/js";
import { useAgentTasks } from "@fencyai/react";
import { useCallback, useState } from "react";
import { isFetchBaseQueryError } from "../errors/isFetchBaseQueryError";
import { useExploreConversation } from "./useExploreConversation";

type UseExploreMemoriesChatProps = {
  model: AgentTaskModel;
  fetchCreateExploreMemoriesClientToken: (conversationId: string) => Promise<{ clientToken: string }>;
  fetchFencyFileDownloadClientToken: (agentTaskFileId: string) => Promise<{ clientToken: string }>;
};

export const useExploreMemoriesChat = ({
  model,
  fetchCreateExploreMemoriesClientToken,
  fetchFencyFileDownloadClientToken,
}: UseExploreMemoriesChatProps) => {
  const [prompt, setPrompt] = useState("");
  const [userMessages, setUserMessages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sendError, setSendError] = useState(false);
  const { ensureConversation, invalidateConversation } = useExploreConversation();

  const fetchExploreMemoriesClientToken = useCallback(
    async (conversationId: string) => {
      try {
        return await fetchCreateExploreMemoriesClientToken(conversationId);
      } catch (error) {
        if (isFetchBaseQueryError(error) && error.status === 403) {
          invalidateConversation();
        }
        throw error;
      }
    },
    [fetchCreateExploreMemoriesClientToken, invalidateConversation]
  );

  const { agentTasks, createAgentTask } = useAgentTasks({
    fetchCreateAgentTaskClientToken: async () => {
      const id = await ensureConversation();
      return fetchExploreMemoriesClientToken(id);
    },
  });

  const handleSend = useCallback(async (message?: string) => {
    // `message` is set when an example chip is clicked; leave the textarea draft untouched in that case.
    const fromTextarea = message === undefined;
    const trimmed = (message ?? prompt).trim();
    if (!trimmed || isLoading) {
      return;
    }

    setSendError(false);
    setUserMessages((prev) => [...prev, trimmed]);
    if (fromTextarea) {
      setPrompt("");
    }
    setIsLoading(true);

    try {
      const conversationId = await ensureConversation();
      const result = await createAgentTask(
        {
          type: "ExploreMemories",
          query: trimmed,
          model,
          language: "no",
        },
        {
          fetchCreateAgentTaskClientToken: () => fetchExploreMemoriesClientToken(conversationId),
          fetchAgentTaskFileDownloadClientToken: async (agentTaskFileId) =>
            fetchFencyFileDownloadClientToken(agentTaskFileId),
        }
      );

      // Keep the message visible and surface an error rather than snapping back to the empty state.
      if (result.type !== "success") {
        setSendError(true);
      }
    } catch {
      setSendError(true);
    } finally {
      setIsLoading(false);
    }
  }, [
    createAgentTask,
    ensureConversation,
    fetchExploreMemoriesClientToken,
    fetchFencyFileDownloadClientToken,
    isLoading,
    model,
    prompt,
  ]);

  return {
    userMessages,
    agentTasks,
    prompt,
    setPrompt,
    isLoading,
    sendError,
    handleSend,
  };
};
